import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../../context/configurationContext';

import css from './FilterPills.module.css';

/**
 * The filter pills under Featured Rentals — Occasion, Designer, Size, Colour —
 * as the Figma draws them. Each option is a plain link into search with that
 * filter applied: the homepage does no filtering of its own, it hands the
 * intent to the search page, which is where filtering already works.
 *
 * Options come from hosted config, so a new designer or occasion added in
 * Console appears here with no code change — same rule as BrowseNav.
 */

// The Figma shows these four, in this order. Multi-enums search with has_any.
const PILLS = [
  { key: 'occasion', label: 'Occasion', multi: true },
  { key: 'designer', label: 'Designer', multi: false },
  { key: 'size_clothing', label: 'Size', multi: false },
  { key: 'color', label: 'Colour', multi: true },
];

const searchHref = (key, option, multi) =>
  `/s?pub_${key}=${multi ? `has_any:${encodeURIComponent(option)}` : encodeURIComponent(option)}`;

const Pill = props => {
  const { label, fieldKey, options, multi } = props;
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onDocumentClick = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [isOpen]);

  return (
    <div className={css.pillWrapper} ref={rootRef}>
      <button
        type="button"
        className={classNames(css.pill, { [css.pillOpen]: isOpen })}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={() => setIsOpen(open => !open)}
      >
        {label}
        <span className={css.chevron} aria-hidden="true" />
      </button>
      {isOpen ? (
        <ul className={css.menu}>
          {options.map(o => (
            <li key={o.option}>
              <a className={css.menuItem} href={searchHref(fieldKey, o.option, multi)}>
                {o.label}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

/**
 * @component
 * @param {Object} props
 * @param {string?} props.className extra style rules
 * @returns {JSX.Element|null} null when no configured field has options
 */
const FilterPills = props => {
  const { className } = props;
  const config = useConfiguration();
  const listingFields = config?.listing?.listingFields || [];

  const pills = PILLS.map(pill => {
    const field = listingFields.find(f => f.key === pill.key);
    return { ...pill, options: field?.enumOptions || [] };
  }).filter(pill => pill.options.length > 0);

  if (pills.length === 0) {
    return null;
  }

  return (
    <div className={classNames(css.root, className)}>
      {pills.map(pill => (
        <Pill
          key={pill.key}
          label={pill.label}
          fieldKey={pill.key}
          options={pill.options}
          multi={pill.multi}
        />
      ))}
    </div>
  );
};

export default FilterPills;
